import { pick } from 'lodash';

import { FieldOptionName } from '../../interfaces/Scan';
import { sortSeverities } from '../../services/severities/SeverityService';
import { OptionType } from '../../types/OptionType';

export interface IFilterOptions {
	components?: OptionType[];
	types?: OptionType[];
	severities?: OptionType[];
	searchValue?: string;
	urls?: OptionType[];
	tags?: OptionType[];
	collectionTypes?: OptionType[];
	collections?: OptionType[];
	liftPeriods?: OptionType[];
}

const emptyFilterOptions: IFilterOptions = {
	types: [],
	severities: [],
	searchValue: '',
	components: [],
	urls: [],
	tags: [],
	collectionTypes: [],
	collections: [],
	liftPeriods: []
};

// get only those filter types which are gonna be visible on the UI
const getVisibleFilterKeys = ({
	allOptions,
	textSearchEnabled,
	severitiesFilterEnabled = true,
	typesFilterEnabled = true,
	tagsFilterEnabled = true
}: {
	allOptions: IFilterOptions;
	textSearchEnabled?: boolean;
	severitiesFilterEnabled?: boolean;
	typesFilterEnabled?: boolean;
	tagsFilterEnabled?: boolean;
}): string[] => {
	const keys = Object.keys(allOptions ?? {}).filter((key) => {
		if (key === 'searchValue') {
			return textSearchEnabled;
		}

		if (key === 'severities' && !severitiesFilterEnabled) {
			return false;
		}

		if (key === 'types' && !typesFilterEnabled) {
			return false;
		}

		if (key === 'tags' && !tagsFilterEnabled) {
			return false;
		}

		return !!allOptions[key]?.length;
	});

	// the most reliable way to make sure we have the searchValue key regardless of
	// allOptions from the backend because text search is only controlled by textSearchEnabled
	if (textSearchEnabled && !keys.includes('searchValue')) {
		keys.push('searchValue');
	}

	return keys;
};

const getEmptyFilterOptions = (params: {
	allOptions: IFilterOptions;
	textSearchEnabled?: boolean;
	severitiesFilterEnabled?: boolean;
	typesFilterEnabled?: boolean;
	tagsFilterEnabled?: boolean;
}): IFilterOptions => pick(emptyFilterOptions, ...getVisibleFilterKeys(params));

export const orderByMethod = (values: string[] = [], option = ''): string[] => {
	if (option === FieldOptionName.SEVERITY) {
		return sortSeverities(values);
	}
	return values.sort();
};

export const getAppliedFiltersInitialValue = (
	allFilters: IFilterOptions,
	selectedType: string,
	selectedSeverity: string,
	textSearchEnabled?: boolean,
	severitiesFilterEnabled?: boolean,
	typesFilterEnabled?: boolean,
	tagsFilterEnabled?: boolean
): IFilterOptions => {
	const filters = getEmptyFilterOptions({
		allOptions: allFilters,
		textSearchEnabled,
		severitiesFilterEnabled,
		typesFilterEnabled,
		tagsFilterEnabled
	});

	if (selectedType) {
		filters.types = [allFilters?.types.find((option) => option.value === selectedType)];
	}
	if (selectedSeverity) {
		filters.severities = [
			allFilters?.severities.find((option) => option.value === selectedSeverity)
		];
	}
	return filters;
};
